import { vec3 } from 'gl-matrix';
import { ViewportType } from '../../../enums/index.js';
import renderingEngineCache from '../../renderingEngineCache.js';
import GenericViewport from '../GenericViewport.js';
import { getDimensionGroupReferenceContext, } from '../genericViewportReferenceCompatibility.js';
import { getGenericViewportImageDisplaySet, isGenericViewportImageDisplaySet, } from '../genericViewportDisplaySetAccess.js';
import { DefaultVolume3DDataProvider } from './DefaultVolume3DDataProvider.js';
import { createVolume3DRenderPathResolver } from './Volume3DRenderPathResolver.js';
import Volume3DResolvedView from './Volume3DResolvedView.js';
import applyVolume3DCamera from './applyVolume3DCamera.js';
import { getVolume3DProjectionSnapshot, } from './volume3DProjectionAdapter.js';
class VolumeViewport3D extends GenericViewport {
    static get useCustomRenderingPipeline() {
        return false;
    }
    getUseCustomRenderingPipeline() {
        return false;
    }
    setRendered() {
        super.setRendered();
    }
    constructor(args) {
        super(args);
        this.type = ViewportType.VOLUME_3D_NEXT;
        this.suppressEvents = false;
        this.renderingEngineId = args.renderingEngineId;
        this.canvas = args.canvas;
        this.canvas.style.display = '';
        this.sWidth = args.sWidth;
        this.sHeight = args.sHeight;
        this.defaultOptions = args.defaultOptions || {};
        this.element.style.position = this.element.style.position || 'relative';
        this.element.style.overflow = 'hidden';
        this.element.style.background = this.element.style.background || '#000';
        this.dataProvider = args.dataProvider || new DefaultVolume3DDataProvider();
        this.renderPathResolver =
            args.renderPathResolver || createVolume3DRenderPathResolver();
        const renderingEngine = renderingEngineCache.get(this.renderingEngineId);
        const renderer = renderingEngine?.getRenderer(this.id);
        if (!renderer) {
            throw new Error('[VolumeViewport3D] No renderer available. Ensure WebGL is supported and the rendering engine has been properly initialized.');
        }
        renderer
            .getActiveCamera()
            .setParallelProjection(this.defaultOptions.parallelProjection ?? true);
        this.renderContext = {
            viewportId: this.id,
            renderingEngineId: this.renderingEngineId,
            type: '3d',
            viewport: {
                element: this.element,
                options: {
                    orientation: this.defaultOptions.orientation,
                    parallelProjection: this.defaultOptions.parallelProjection,
                },
            },
            display: {
                requestRender: () => {
                    this.requestRenderingEngineRender();
                },
            },
            vtk: {
                canvas: this.canvas,
                renderer,
            },
        };
        this.viewState = {
            parallelProjection: this.defaultOptions.parallelProjection ?? true,
        };
        this.element.setAttribute('data-viewport-uid', this.id);
        this.element.setAttribute('data-rendering-engine-uid', this.renderingEngineId);
    }
    async setDisplaySets(...entries) {
        this.removeAllData();
        for (const [index, { displaySetId, options = {} }] of entries.entries()) {
            await this.addDisplaySet(displaySetId, {
                ...options,
                role: options.role ?? (index === 0 ? 'source' : 'overlay'),
            });
        }
    }
    async addDisplaySet(displaySetId, options = {}) {
        const volumeOptions = options;
        const renderMode = this.resolveRenderMode(displaySetId, volumeOptions.renderMode);
        await super.addDisplaySet(displaySetId, {
            renderMode,
            role: volumeOptions.role,
        });
        if (renderMode === 'vtkVolume3d' && volumeOptions.role === 'source') {
            this.primaryDataId = displaySetId;
        }
        this.setDefaultDataPresentation(displaySetId, {
            visible: true,
            opacity: 1,
        });
        this.viewState = this.getViewState();
    }
    getRenderingEngine() {
        return renderingEngineCache.get(this.renderingEngineId);
    }
    getImageIds() {
        const binding = this.getCurrentBinding();
        if (!binding) {
            return [];
        }
        const data = this.getVolume3DPayload(binding);
        const rendering = this.getVolume3DRendering(binding);
        if (!data ||
            data.renderMode !== 'vtkVolume3d' ||
            rendering.renderMode !== 'vtkVolume3d') {
            return [];
        }
        return data.imageIds;
    }
    getRenderer() {
        return this.renderContext.vtk.renderer;
    }
    getCanvas() {
        return this.canvas;
    }
    getVtkActiveCamera() {
        return this.getRenderer().getActiveCamera();
    }
    getViewState() {
        return this.getRuntimeCamera();
    }
    setViewState(viewStatePatch) {
        if (this.isDestroyed) {
            return;
        }
        const previousCamera = this.getCameraForEvent();
        applyVolume3DCamera(this.renderContext, viewStatePatch, {
            resetClippingRange: true,
        });
        this.viewState = this.getRuntimeCamera();
        this.modified(previousCamera);
    }
    getRuntimeCamera() {
        const camera = this.getRenderer().getActiveCamera();
        return {
            clippingRange: camera.getClippingRange(),
            focalPoint: camera.getFocalPoint(),
            parallelProjection: camera.getParallelProjection(),
            parallelScale: camera.getParallelScale(),
            position: camera.getPosition(),
            rotation: 0,
            viewAngle: camera.getViewAngle(),
            viewPlaneNormal: camera.getViewPlaneNormal(),
            viewUp: camera.getViewUp(),
        };
    }
    getResolvedView() {
        return new Volume3DResolvedView({
            camera: this.getViewState(),
            canvas: this.canvas,
            frameOfReferenceUID: this.resolveFrameOfReferenceUID(),
            renderer: this.getRenderer(),
        });
    }
    getViewReference(_viewRefSpecifier = {}) {
        const binding = this.getCurrentBinding();
        const data = binding ? this.getVolume3DPayload(binding) : undefined;
        const camera = this.getViewState();
        const FrameOfReferenceUID = this.getFrameOfReferenceUID();
        const cameraFocalPoint = camera.focalPoint;
        const viewPlaneNormal = camera.viewPlaneNormal;
        const viewUp = camera.viewUp;
        const viewReference = {
            FrameOfReferenceUID,
            dataId: binding?.data.id,
            cameraFocalPoint,
            viewPlaneNormal,
            viewUp,
        };
        if (data?.renderMode === 'vtkVolume3d') {
            viewReference.volumeId = data.volumeId;
            Object.assign(viewReference, getDimensionGroupReferenceContext(data.imageVolume));
        }
        if (cameraFocalPoint && viewPlaneNormal && viewUp) {
            viewReference.planeRestriction = {
                FrameOfReferenceUID,
                point: cameraFocalPoint,
                inPlaneVector1: viewUp,
                inPlaneVector2: vec3.cross(vec3.create(), viewUp, viewPlaneNormal),
            };
        }
        return viewReference;
    }
    getVolumeId() {
        const binding = this.getCurrentBinding();
        if (!binding) {
            return;
        }
        const data = this.getVolume3DPayload(binding);
        const rendering = this.getVolume3DRendering(binding);
        if (!data ||
            data.renderMode !== 'vtkVolume3d' ||
            rendering.renderMode !== 'vtkVolume3d') {
            return;
        }
        return data.volumeId;
    }
    hasVolumeId(volumeId) {
        return this.getActors().some((actorEntry) => actorEntry.referencedId === volumeId);
    }
    hasVolumeURI(volumeURI) {
        return this.getActors().some((actorEntry) => String(actorEntry.referencedId || '').includes(volumeURI));
    }
    getImageData() {
        return this.getCurrentBinding()?.getImageData?.();
    }
    getActors() {
        const actors = [];
        for (const binding of this.bindings.values()) {
            const data = this.getVolume3DPayload(binding);
            if (!data) {
                continue;
            }
            actors.push(...this.getActorEntriesForRendering(this.getVolume3DRendering(binding), data));
        }
        return actors;
    }
    getDefaultActor() {
        const binding = this.getCurrentBinding();
        if (!binding) {
            return this.getActors()[0];
        }
        const data = this.getVolume3DPayload(binding);
        if (!data) {
            return undefined;
        }
        return this.getActorEntriesForRendering(this.getVolume3DRendering(binding), data)[0];
    }
    resetViewState(options) {
        const { resetPan = true, resetZoom = true, resetToCenter = true, } = options || {};
        const previousCamera = this.getCameraForEvent();
        const renderer = this.getRenderer();
        const camera = renderer.getActiveCamera();
        const previousParallelScale = camera.getParallelScale();
        const previousPosition = camera.getPosition();
        const previousFocalPoint = camera.getFocalPoint();
        renderer.resetCamera();
        renderer.resetCameraClippingRange();
        if (!resetZoom) {
            camera.setParallelScale(previousParallelScale);
        }
        if (!resetPan || !resetToCenter) {
            camera.setFocalPoint(...previousFocalPoint);
            camera.setPosition(...previousPosition);
            renderer.resetCameraClippingRange();
        }
        this.viewState = this.getViewState();
        this.render();
        this.triggerCameraModifiedEvent(previousCamera);
        this.triggerCameraResetEvent();
        return true;
    }
    resetViewStateForResize() {
        return this.resetViewState();
    }
    resize() {
        if (this.isDestroyed) {
            return;
        }
        this.sWidth = this.canvas.width;
        this.sHeight = this.canvas.height;
        this.resizeBindings();
    }
    render() {
        if (this.isDestroyed) {
            return;
        }
        if (!this.renderBindings()) {
            this.requestRenderingEngineRender();
        }
    }
    onDestroy() {
        this.primaryDataId = undefined;
    }
    getProjectionSnapshot() {
        const snapshot = getVolume3DProjectionSnapshot({
            viewport: this,
            canvasHeight: this.canvas.clientHeight || this.element.clientHeight,
            canvasWidth: this.canvas.clientWidth || this.element.clientWidth,
            camera: this.getViewState(),
            frameOfReferenceUID: this.resolveFrameOfReferenceUID(),
            resolvedView: this.getResolvedView(),
        });
        if (!snapshot) {
            throw new Error('[VolumeViewport3D] Unable to resolve projection snapshot');
        }
        return snapshot;
    }
    getCurrentBinding() {
        if (this.primaryDataId) {
            return this.getBinding(this.primaryDataId) ?? this.getFirstBinding();
        }
        return this.getFirstBinding();
    }
    getReferenceViewContexts() {
        const contexts = [];
        const camera = this.getViewState();
        for (const [dataId, binding] of this.bindings.entries()) {
            const data = this.getVolume3DPayload(binding);
            const volumeId = data?.renderMode === 'vtkVolume3d' ? data.volumeId : undefined;
            contexts.push({
                dataId,
                dataIds: [binding.data.id],
                frameOfReferenceUID: binding.getFrameOfReferenceUID() ?? this.getFrameOfReferenceUID(),
                imageIds: data?.renderMode === 'vtkVolume3d' ? data.imageIds : undefined,
                volumeId,
                volumeIds: volumeId ? [volumeId] : undefined,
                cameraFocalPoint: camera.focalPoint,
                viewPlaneNormal: camera.viewPlaneNormal,
                ...(data?.renderMode === 'vtkVolume3d'
                    ? getDimensionGroupReferenceContext(data.imageVolume)
                    : {}),
            });
        }
        return contexts.length ? contexts : super.getReferenceViewContexts();
    }
    requestRenderingEngineRender() {
        const renderingEngine = renderingEngineCache.get(this.renderingEngineId);
        if (renderingEngine) {
            renderingEngine.renderViewport(this.id);
        }
    }
    resolveRenderMode(dataId, requestedRenderMode = 'auto') {
        if (requestedRenderMode && requestedRenderMode !== 'auto') {
            return requestedRenderMode;
        }
        const dataSet = this.getDataSet(dataId);
        if (dataSet?.imageIds?.length) {
            return 'vtkVolume3d';
        }
        return 'vtkGeometry3d';
    }
    getDataSet(dataId) {
        const dataSet = getGenericViewportImageDisplaySet(dataId);
        if (!isVolume3DRegisteredDataSet(dataSet)) {
            return;
        }
        return dataSet;
    }
    getVolume3DPayload(binding) {
        if (!isVolume3DData(binding.data)) {
            return;
        }
        return binding.data;
    }
    resolveFrameOfReferenceUID() {
        const binding = this.getCurrentBinding();
        if (!binding) {
            return;
        }
        const data = this.getVolume3DPayload(binding);
        const rendering = this.getVolume3DRendering(binding);
        if (!data) {
            return;
        }
        if (data.renderMode === 'vtkVolume3d') {
            return data.imageVolume.metadata?.FrameOfReferenceUID;
        }
        if (rendering.renderMode === 'vtkGeometry3d') {
            return rendering.frameOfReferenceUID;
        }
    }
    getVolume3DRendering(binding) {
        if (!isVolume3DRendering(binding.rendering)) {
            throw new Error('[VolumeViewport3D] Binding render mode is not a supported 3D rendering');
        }
        return binding.rendering;
    }
    getActorEntriesForRendering(rendering, data) {
        if (rendering.renderMode === 'vtkVolume3d' &&
            data.renderMode === 'vtkVolume3d') {
            return [
                {
                    actor: rendering.actor,
                    referencedId: data.volumeId,
                    uid: rendering.actorEntryUID,
                },
            ];
        }
        if (rendering.renderMode === 'vtkGeometry3d') {
            return rendering.actors;
        }
        return [];
    }
}
export default VolumeViewport3D;
function isVolume3DData(data) {
    if (typeof data !== 'object' || data === null) {
        return false;
    }
    const payload = data;
    return ((payload.type === 'image' && payload.renderMode === 'vtkVolume3d') ||
        (payload.type === 'geometry' && payload.renderMode === 'vtkGeometry3d'));
}
function isVolume3DRendering(rendering) {
    return (rendering.renderMode === 'vtkVolume3d' ||
        rendering.renderMode === 'vtkGeometry3d');
}
function isVolume3DRegisteredDataSet(value) {
    if (!isGenericViewportImageDisplaySet(value)) {
        return false;
    }
    return ((value.geometryId === undefined || typeof value.geometryId === 'string') &&
        (value.volumeId === undefined || typeof value.volumeId === 'string') &&
        (value.geometryLoadOptions === undefined ||
            (typeof value.geometryLoadOptions === 'object' &&
                !Array.isArray(value.geometryLoadOptions))));
}
